import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import Typography from "./base/typography";
import { Box } from "@mui/system";
import { useRef, useContext, useEffect, useState } from "react";
import { countries } from "country-data";
import UserContext from "../services/user/user";
import Exporter from "./_elements/exporter";

import TrendArrow from "./_elements/TrendArrow";
import APIContext from "../services/api/api";
import { EcUserDataContext } from "@eclear-ag/ec-common-ui";

import { FormattedMessage } from "react-intl";
import EInfoToolTip from "./_elements/eInfo-tooltip";

export default function TopTable({
  dataUrl,
  titleId,
  tooltipTextId,
  csvLink,
  fileName,
  className,
  type,
  testIdPrefix = "top-table"
}) {
  const { getData } = useContext(APIContext);
  const { currency, formatNumber } = useContext(UserContext);
  const { userData } = useContext(EcUserDataContext);

  const [tableData, setTableData] = useState([{}, {}, {}, {}, {}]);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);

  const [productRow, setProductRow] = useState(false);

  const emptyData = [{}, {}, {}, {}, {}];

  const tableRef = useRef();

  useEffect(() => {
    async function fetchData() {
      const tableDataRes = await getData(dataUrl, {
        "merchant-id": userData.companyId
      });
      if (tableDataRes === 500 || tableDataRes === 504) {
        setIsEmpty(true);
        setTableData(emptyData);
        setIsTableLoading(false);
      } else {
        const data = tableDataRes;
        setTableData(data);
        data.length === 0 ? setIsEmpty(true) : setIsEmpty(false);
        setIsTableLoading(false);
      }
    }

    if (userData.companyId) {
      fetchData();
    }
  }, [userData, dataUrl]);

  useEffect(() => {
    document.querySelector(`.product-0`) && setProductRow(!productRow);
  }, [document.querySelector(`.product-0`)]);

  // Generate headers based on type
  const getHeaders = () => {
    if (type === "countries") {
      return [
        { label: "Rank", key: "rank" },
        { label: "Country", key: "toCountry" },
        { label: "Current", key: "************" },
        { label: "Prev", key: "************" },
        { label: "Trend %", key: "*****************" }
      ];
    } else {
      return [
        { label: "Rank", key: "rank" },
        { label: "Product", key: "***********" },
        { label: "Current", key: "************" },
        { label: "Prev", key: "************" },
        { label: "Trend %", key: "*****************" }
      ];
    }
  };

  // Get column header translation key
  const getColumnHeaderKey = () => {
    return type === "countries" ? "country" : "products";
  };

  // Render the name/flag cell based on type
  const renderNameCell = (row) => {
    if (type === "countries") {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            fontWeight: 400
          }}
        >
          <Box
            sx={{
              mr: 1,
              width: "16px",
              height: "16px",
              objectFit: "contain",
              borderRadius: "50%",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <img
              alt="country flag"
              src={`https://hatscripts.github.io/circle-flags/flags/${row.toCountry.toLowerCase()}.svg`}
              style={{
                width: "16px",
                height: "16px",
                objectFit: "contain"
              }}
            />
          </Box>
          {countries[row.toCountry].name}
        </Box>
      );
    } else {
      return row.fromProduct;
    }
  };

  // Get table cell alignment for name column
  const getNameCellAlignment = () => {
    return type === "countries" ? "left" : "center";
  };

  // Header cell style shared across all header columns (.Table-Cell).
  // Per-column overrides (justifyContent / minWidth) are merged at the call site.
  const headerCellStyles = {
    height: "40px",
    padding: "8px 16px",
    borderBottom: "solid 1px var(--app-layer-accent-01)",
    fontFamily: "Inter",
    fontSize: "12px",
    fontWeight: 600,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: 1.67,
    letterSpacing: "normal",
    textAlign: "left",
    color: "var(--font-strong-default)"
  };

  // Get table cell styles for name column
  const getNameCellStyles = () => {
    const baseStyles = {
      borderBottom: "solid 1px var(--app-layer-accent-01)",
      fontWeight: 400,
      fontSize: "14px",
      color: "var(--font-extreme-default)",
      padding: "16px"
    };

    if (type === "countries") {
      return {
        ...baseStyles,
        textAlign: "left"
      };
    } else {
      return {
        ...baseStyles,
        textAlign: "center",
        width: "40%"
      };
    }
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px"
      }}
      className={`widget-col-6 widget-row-2 ${className} card-main ${
        isEmpty && "no-data"
      }`}
      elevation={0}
      ref={tableRef}
      data-testid={`${testIdPrefix}_box`}
    >
      <Box
        sx={{
          height: "52px",
          alignSelf: "stretch",
          flexGrow: 0,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 16px",
          borderBottom: "solid 1px var(--new-border)"
        }}
      >
        <Typography
          className={`card-title ${isEmpty && "no-data"}`}
          component="h2"
          data-testid={`${testIdPrefix}_heading`}
          sx={{
            fontSize: "20px",
            lineHeight: "28px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            color: isEmpty && "var(--font-medium-default)"
          }}
        >
          <FormattedMessage id={titleId} />
          <EInfoToolTip
            opacity={1}
            darkModeSupport
            status={isEmpty && "noData"}
            data-testid={`${testIdPrefix}_tooltip`}
            msg={
              <ul className="tooltip-list">
                <li>
                  <FormattedMessage id={tooltipTextId} />
                </li>
              </ul>
            }
          />
        </Typography>

        {isTableLoading ? (
          ""
        ) : (
          <Box sx={{ display: "flex" }}>
            <Exporter
              isEmpty={isEmpty}
              fileName={fileName}
              refVal={tableRef}
              csvLink={csvLink}
              data={tableData}
              headers={getHeaders()}
              downloadButtonTestId={`${testIdPrefix}_export_button`}
              pngButtonTestId={`${testIdPrefix}_png_button`}
            />
          </Box>
        )}
      </Box>
      <Box sx={{ textAlign: "center" }}>
        {isTableLoading ? (
          <CircularProgress />
        ) : (
          <TableContainer ref={tableRef}>
            <Table aria-label={`${type} table`} data-testid={`${testIdPrefix}`}>
              <TableHead
                data-testid={`${testIdPrefix}_head`}
              >
                <TableRow data-testid={`${testIdPrefix}_row_header`}>
                  <TableCell
                    className={`${isEmpty && "no-data"}`}
                    data-testid={`${testIdPrefix}_header_rank`}
                    sx={{
                      ...headerCellStyles,
                      ...(type === "countries" && { width: "62px" })
                    }}
                  >
                    <FormattedMessage id="rank" />
                  </TableCell>
                  <TableCell
                    className={`${isEmpty && "no-data"}`}
                    data-testid={`${testIdPrefix}_header_name`}
                    sx={{
                      ...headerCellStyles,
                      textAlign: type === "countries" ? "left" : "center",
                      ...(type === "countries" && { width: "161.5px" })
                    }}
                  >
                    <span
                      style={{
                        marginLeft: type === "countries" ? "8px" : "0px"
                      }}
                    >
                      <FormattedMessage id={getColumnHeaderKey()} />
                    </span>
                  </TableCell>
                  <TableCell
                    className={`${isEmpty && "no-data"}`}
                    data-testid={`${testIdPrefix}_header_current`}
                    sx={{
                      ...headerCellStyles,
                      ...(type === "countries" && { width: "161.5px" })
                    }}
                  >
                    <FormattedMessage id="current" />
                  </TableCell>
                  <TableCell
                    className={`${isEmpty && "no-data"}`}
                    data-testid={`${testIdPrefix}_header_prev`}
                    sx={{
                      ...headerCellStyles,
                      minWidth: "60px",
                      ...(type === "countries" && { width: "70px" })
                    }}
                  >
                    <FormattedMessage id="prev" />
                  </TableCell>
                  <TableCell
                    className={`${isEmpty && "no-data"}`}
                    data-testid={`${testIdPrefix}_header_trend`}
                    sx={{
                      ...headerCellStyles,
                      textAlign: "right",
                      minWidth: type === "products" ? "120px" : "auto",
                      ...(type === "countries" && { width: "91px" })
                    }}
                  >
                    <FormattedMessage id="trend" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody data-testid={`${testIdPrefix}_body`}>
                {isEmpty === true
                  ? emptyData.map((row, index) => (
                      <TableRow
                        hover
                        key={index}
                        style={{ height: "40px" }}
                        data-testid={`${testIdPrefix}-row-${index}`}
                      >
                        <TableCell
                          className={`${isEmpty && "no-data"}`}
                          data-testid={`${testIdPrefix}_empty_cell_rank_${index}`}
                          sx={{
                            borderBottom: "solid 1px var(--app-layer-accent-01)",
                            textAlign: "left",
                            fontWeight: 400,
                            padding: "16px",
                            fontSize: "14px"
                          }}
                        >
                          {index + 1}
                        </TableCell>
                        <TableCell
                          data-testid={`${testIdPrefix}_empty_cell_name_${index}`}
                          sx={{
                            borderBottom: "solid 1px var(--app-layer-accent-01)",
                            fontWeight: 400,
                            padding: "16px",
                            fontSize: "14px"
                          }}
                        ></TableCell>
                        <TableCell
                          data-testid={`${testIdPrefix}_empty_cell_current_${index}`}
                          sx={{
                            borderBottom: "solid 1px var(--app-layer-accent-01)",
                            textAlign: "left",
                            fontWeight: 400,
                            padding: "16px",
                            fontSize: "14px"
                          }}
                        ></TableCell>
                        <TableCell
                          data-testid={`${testIdPrefix}_empty_cell_prev_${index}`}
                          sx={{
                            borderBottom: "solid 1px var(--app-layer-accent-01)",
                            textAlign: "left",
                            fontWeight: 400,
                            padding: "16px"
                          }}
                        ></TableCell>
                        <TableCell
                          data-testid={`${testIdPrefix}_empty_cell_trend_${index}`}
                          style={{
                            borderBottom: "solid 1px var(--app-layer-accent-01)",
                            padding: "16px",
                            fontSize: "14px"
                          }}
                        ></TableCell>
                      </TableRow>
                    ))
                  : tableData?.map((row, index) => (
                      <TableRow
                        hover
                        key={type === "countries" ? index : row.fromProduct}
                        data-testid={`${testIdPrefix}_row_${index}`}
                      >
                        <TableCell
                          data-testid={`${testIdPrefix}_cell_rank_${index}`}
                          sx={{
                            borderBottom: "solid 1px var(--app-layer-accent-01)",
                            textAlign: "left",
                            fontWeight: 400,
                            padding: "16px",
                            fontSize: "14px"
                          }}
                        >
                          {index + 1}
                        </TableCell>
                        <TableCell
                          data-testid={`${testIdPrefix}_cell_name_${index}`}
                          sx={getNameCellStyles()}
                        >
                          {renderNameCell(row)}
                        </TableCell>
                        <TableCell
                          data-testid={`${testIdPrefix}_cell_current_${index}`}
                          sx={{
                            borderBottom: "solid 1px var(--app-layer-accent-01)",
                            textAlign: "left",
                            fontWeight: 400,
                            padding: "16px",
                            fontSize: "14px"
                          }}
                        >
                          {formatNumber(row.totalRevenue, 0) + currency.symbol}
                        </TableCell>
                        <TableCell
                          data-testid={`${testIdPrefix}_cell_prev_${index}`}
                          sx={{
                            borderBottom: "solid 1px var(--app-layer-accent-01)",
                            textAlign: "left",
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "var(--font-extreme-default)",
                            padding: "16px"
                          }}
                        >
                          {row.prevPosition || "-"}
                        </TableCell>
                        <TableCell
                          data-testid={`${testIdPrefix}_cell_trend_${index}`}
                          sx={{
                            borderBottom: "solid 1px var(--app-layer-accent-01)",
                            padding: "16px",
                            fontSize: "14px"
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: "6px",
                              fontWeight: 400,
                              fontSize: "14px"
                            }}
                          >
                            {typeof row.comparePercentage !== "undefined" ? (
                              <>
                                <span
                                  style={{
                                    fontWeight: 400,
                                    fontSize: "14px",
                                    color:
                                      row.comparePercentage < 0
                                        ? "var(--font-heavy-error)"
                                        : row.comparePercentage > 0
                                          ? "var(--font-strong-success)"
                                          : "var(--font-extreme-default)"
                                  }}
                                >
                                  {row.comparePercentage > 0 ? "+" : ""}
                                  {row.comparePostion
                                    ? formatNumber(row.comparePercentage, 0) + "%"
                                    : "100%"}
                                </span>
                                {typeof row.comparePostion !== "undefined" && (
                                  <TrendArrow
                                    direction={row.comparePostion}
                                    size={20}
                                    data-testid={`${testIdPrefix}_trend_arrow_${index}`}
                                  />
                                )}
                              </>
                            ) : (
                              <span className="text">-</span>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Paper>
  );
}
